import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { createLead } from "@/lib/api";
import { BUDGETS, PROPERTY_TYPES, SERVICE_OPTIONS } from "@/lib/config";

const field =
  "w-full bg-transparent border-b border-ink/20 rounded-none px-0 py-3 focus:border-bronze focus:outline-none text-base placeholder:text-ink/40 transition-colors";
const labelC = "overline text-bronze block mb-2";

export default function LeadForm({ variant = "full", defaultService = "", source = "Website Enquiry", showDateTime = false }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "",
    budget: "", property_type: "", service_required: defaultService,
    message: "", preferred_date: "", preferred_time: "",
  });

  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await createLead({ ...form, lead_source: source });
      setDone(true);
      toast.success("Thank you! Our team will reach out shortly.");
      setForm({ name: "", phone: "", email: "", location: "", budget: "", property_type: "", service_required: defaultService, message: "", preferred_date: "", preferred_time: "" });
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div data-testid="lead-form-success" className="border border-gold/40 bg-cream/60 p-10 rounded-md text-center">
        <p className="font-display text-4xl text-ink">Thank you.</p>
        <p className="mt-3 text-ink/70">Your enquiry has reached our studio. A Relationship Manager will connect with you within 24 hours.</p>
        <button onClick={() => setDone(false)} className="mt-6 text-sm text-bronze link-underline" data-testid="lead-form-reset">Submit another enquiry</button>
      </div>
    );

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-7">
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className={labelC}>Name*</label>
          <input data-testid="lead-name" className={field} value={form.name} onChange={up("name")} placeholder="Your full name" />
        </div>
        <div>
          <label className={labelC}>Phone*</label>
          <input data-testid="lead-phone" className={field} value={form.phone} onChange={up("phone")} placeholder="+91" />
        </div>
        <div>
          <label className={labelC}>Email</label>
          <input data-testid="lead-email" type="email" className={field} value={form.email} onChange={up("email")} placeholder="you@email.com" />
        </div>
        <div>
          <label className={labelC}>Location</label>
          <input data-testid="lead-location" className={field} value={form.location} onChange={up("location")} placeholder="Area, City" />
        </div>
      </div>

      {variant === "full" && (
        <div className="grid sm:grid-cols-3 gap-6">
          <div>
            <label className={labelC}>Property Type</label>
            <select data-testid="lead-property" className={field} value={form.property_type} onChange={up("property_type")}>
              <option value="">Select</option>
              {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className={labelC}>Budget</label>
            <select data-testid="lead-budget" className={field} value={form.budget} onChange={up("budget")}>
              <option value="">Select</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className={labelC}>Service Required</label>
            <select data-testid="lead-service" className={field} value={form.service_required} onChange={up("service_required")}>
              <option value="">Select</option>
              {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}

      {showDateTime && (
        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <label className={labelC}>Preferred Date</label>
            <input data-testid="lead-date" type="date" className={field} value={form.preferred_date} onChange={up("preferred_date")} />
          </div>
          <div>
            <label className={labelC}>Preferred Time</label>
            <select data-testid="lead-time" className={field} value={form.preferred_time} onChange={up("preferred_time")}>
              <option value="">Select</option>
              {["10:00 AM", "12:00 PM", "02:00 PM", "04:00 PM", "06:00 PM"].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
      )}

      <div>
        <label className={labelC}>Message</label>
        <textarea data-testid="lead-message" rows={3} className={field} value={form.message} onChange={up("message")} placeholder="Tell us about your project…" />
      </div>

      <button
        type="submit"
        data-testid="lead-submit"
        disabled={loading}
        className="inline-flex items-center gap-2 bg-olive text-beige text-sm uppercase tracking-[0.2em] font-bold px-10 py-4 rounded-sm hover:bg-[#21463c] transition-colors duration-300 disabled:opacity-60"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        {showDateTime ? "Request Appointment" : "Submit Enquiry"}
      </button>
    </form>
  );
}
